import React from 'react';
import {
  Box,
  Text,
  Badge,
  Card,
  CardBody,
  EditIcon,
  TrashIcon,
  Dropdown,
  DropdownOverlay,
  ActionList,
  ActionListItem,
  ActionListItemIcon,
  DropdownButton,
  Switch,
  Divider,
} from '@razorpay/blade/components';
import { AIWorkflowConfig } from '../types';

// Map trigger codes to display names
const TRIGGER_DISPLAY_NAMES: Record<string, string> = {
  reimbursement_created: 'Reimbursement Created',
  leave_created: 'Leave Created',
};

interface WorkflowListProps {
  workflows: AIWorkflowConfig[];
  onEdit: (workflow: AIWorkflowConfig) => void;
  onDelete: (workflow: AIWorkflowConfig) => void;
  onToggle: (workflow: AIWorkflowConfig, isEnabled: boolean) => void;
}

const WorkflowCard = ({
  workflow,
  onEdit,
  onDelete,
  onToggle,
}: {
  workflow: AIWorkflowConfig;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: (isEnabled: boolean) => void;
}) => {
  // Get trigger label from code
  const triggerLabel = workflow.triggerCode 
    ? TRIGGER_DISPLAY_NAMES[workflow.triggerCode] || workflow.triggerCode
    : 'No trigger';
  // Use actionsCount from API response, or fall back to actions array length
  const actionCount = workflow.actionsCount ?? workflow.actions?.length ?? 0;
  const hasDecision = workflow.decision !== null;

  return (
    <Card elevation="lowRaised" padding="spacing.0">
      <CardBody>
        <Box padding="spacing.5">
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" marginBottom="spacing.4">
            <Box display="flex" flexDirection="column" gap="spacing.2">
              <Box display="flex" alignItems="center" gap="spacing.3">
                <Text size="large" weight="semibold">
                  {workflow.name}
                </Text>
                <Badge
                  color={workflow.isEnabled ? 'positive' : 'neutral'}
                  size="small">
                  {workflow.isEnabled ? 'Active' : 'Inactive'}
                </Badge>
              </Box>
              {workflow.description && (
                <Text size="small" color="surface.text.gray.muted">
                  {workflow.description}
                </Text>
              )}
            </Box>

            <Box display="flex" alignItems="center" gap="spacing.3">
              <Switch
                accessibilityLabel="Toggle workflow"
                isChecked={workflow.isEnabled}
                onChange={({ isChecked }) => onToggle(isChecked)}
                size="small"
              />
              <Dropdown>
                <DropdownButton variant="tertiary" size="small">
                  Actions
                </DropdownButton>
                <DropdownOverlay>
                  <ActionList>
                    <ActionListItem
                      title="Edit"
                      value="edit"
                      leading={<ActionListItemIcon icon={EditIcon} />}
                      onClick={onEdit}
                    />
                    <ActionListItem
                      title="Delete"
                      value="delete"
                      leading={<ActionListItemIcon icon={TrashIcon} />}
                      onClick={onDelete}
                      intent="negative"
                    />
                  </ActionList>
                </DropdownOverlay>
              </Dropdown>
            </Box>
          </Box>

          <Divider />

          <Box display="flex" gap="spacing.6" marginTop="spacing.4">
            <Box display="flex" flexDirection="column" gap="spacing.1">
              <Text size="small" color="surface.text.gray.muted">
                Trigger
              </Text>
              <Text size="small" weight="medium">
                {triggerLabel}
              </Text>
            </Box>

            {hasDecision && (
              <Box display="flex" flexDirection="column" gap="spacing.1">
                <Text size="small" color="surface.text.gray.muted">
                  AI Decision
                </Text>
                <Text size="small" weight="medium">
                  Enabled
                </Text>
              </Box>
            )}

            <Box display="flex" flexDirection="column" gap="spacing.1">
              <Text size="small" color="surface.text.gray.muted">
                Actions
              </Text>
              <Text size="small" weight="medium">
                {actionCount} {actionCount === 1 ? 'action' : 'actions'}
              </Text>
            </Box>

            {workflow.updatedAt && (
              <Box display="flex" flexDirection="column" gap="spacing.1" marginLeft="auto">
                <Text size="small" color="surface.text.gray.muted">
                  Last modified
                </Text>
                <Text size="small">
                  {new Date(workflow.updatedAt).toLocaleDateString()}
                </Text>
              </Box>
            )}
          </Box>
        </Box>
      </CardBody>
    </Card>
  );
};

const WorkflowList = ({ workflows, onEdit, onDelete, onToggle }: WorkflowListProps) => {
  return (
    <Box display="flex" flexDirection="column" gap="spacing.4" padding="spacing.6">
      {workflows.map((workflow) => (
        <WorkflowCard
          key={workflow.id}
          workflow={workflow}
          onEdit={() => onEdit(workflow)}
          onDelete={() => onDelete(workflow)}
          onToggle={(isEnabled) => onToggle(workflow, isEnabled)}
        />
      ))}
    </Box>
  );
};

export { WorkflowList };
